import KTextField from "./KTextField";
import {
	VBtn,
	VCard,
	VCardTitle,
	VDatePicker,
	VDialog,
	VMenu,
	VSpacer
} from "vuetify/lib";
import { formatDate } from "kinnect-formatters";

export const defaultMenuProps = {
	bottom: true,
	closeOnContentClick: false,
	fullWidth: true
};

export const defaultTextFieldProps = {
	prependIcon: "event"
};

export const defaultDatePickerProps = {
	color: "primary",
	scrollable: true
};

export default {
	name: "KDatePicker",

	props: {
		menuProps: {
			default: () => defaultMenuProps,
			type: Object
		},

		textFieldProps: {
			default: () => defaultTextFieldProps,
			type: Object
		},

		datePickerProps: {
			default: () => defaultDatePickerProps,
			type: Object
		},

		autosave: {
			default: true,
			type: Boolean
		},

		disabled: Boolean,

		formatToken: null,

		showFieldArrows: Boolean,

		value: {
			required: true
		}
	},

	data: () => ( {
		initialValue: null,
		internalValue: null,
		open: false,
		today: ( new Date() ).setHours( 0, 0, 0, 0 ),
		width: "320px"
	} ),

	computed: {
		$_menuProps() {
			return {
				...defaultMenuProps,
				...this.menuProps,
				disabled: this.disabled,
				maxWidth: this.width,
				minWidth: this.width,
				persistent: this.menuProps.persistent && !this.autosave
			};
		},

		$_textFieldProps() {
			return {
				...defaultTextFieldProps,
				...this.textFieldProps,
				clearable: this.clearable,
				disabled: this.disabled,
				readonly: true,
				validationMode: "aggressive",
				value: this.textFieldValue
			};
		},

		$_datePickerProps() {
			return {
				...defaultDatePickerProps,
				...this.datePickerProps
			};
		},

		clearable() {
			return !this.textFieldProps.required && !this.disabled;
		},

		formattedToday() {
			return formatDate( this.today, "yyyy-MM-dd" );
		},

		textFieldValue() {
			return formatDate( this.value, this.formatToken );
		},

		genPicker() {
			return <VDatePicker
				attrs={{
					...this.$_datePickerProps,
					noTitle: true,
					style: "border-top-left-radius: 0; border-top-right-radius: 0;",
					value: this.internalValue,
					width: this.width
				}}
				onInput={val => {
					this.internalValue = val;

					if ( this.autosave ) {
						this.save();
					}
				}}
			>
				{this.genPickerSlot}
			</VDatePicker>;
		},

		genPickerSlot() {
			return [
				this.clearable && <VBtn
					disabled={!this.internalValue}
					onClick={() => {
						this.internalValue = null;

						if ( this.autosave ) {
							this.save();
						}
					}}
					text
				>Clear</VBtn>,

				<VSpacer></VSpacer>,

				<VBtn
					disabled={this.internalValue === this.formattedToday}
					onClick={() => {
						this.internalValue = this.formattedToday;

						if ( this.autosave ) {
							this.save();
						}
					}}
					text
				>Today</VBtn>,

				!this.autosave && <VBtn
					onClick={this.cancel}
					text
				>Cancel</VBtn>,

				!this.autosave && <VBtn
					color="link"
					onClick={this.save}
					text
				>Save</VBtn>
			];
		},

		genContainer() {
			const TagName = this.$vuetify.breakpoint.smAndDown ? VDialog : VMenu;

			return <TagName
				attrs={{ ...this.$_menuProps }}
				ref={this.$vnode.tag}
				scopedSlots={{
					activator: ( { on } ) =>
						<KTextField
							attrs={{ ...this.$_textFieldProps }}
							directives={this.$vnode.data.directives}
							appendIcon={this.showFieldArrows ? "chevron_right" : null}
							prependInnerIcon={this.showFieldArrows ? "chevron_left" : null}
							on={{
								...on,
								"click:clear": () => this.emitInput( null ),
								"click:prepend-inner": this.goToPrevDay,
								"click:append": this.goToNextDay
							}}
						></KTextField>
				}}
				vModel={this.open}
			>
				{this.genTextField}

				<VCard>
					<VCardTitle class="text-subtitle-1 primary white--text">
						{this.$_textFieldProps.label}
					</VCardTitle>

					{this.genPicker}
				</VCard>
			</TagName>;
		}
	},

	watch: {
		open( open ) {
			if ( open ) {
				this.initialValue = this.value;
				this.internalValue = this.value;
			} else {
				this.internalValue = this.value;
			}
		}
	},

	methods: {
		emitInput( val ) {
			this.$emit( "input", val );
			this.internalValue = val;
			this.$refs[this.$vnode.tag].save( val );
		},

		subtractDay( isoString, numDays = 1 ) {
			return formatDate( new Date( `${isoString}T00:00` ).getTime() - ( numDays * 24 * 60 * 60 * 1000 ), "yyyy-MM-dd" );
		},

		addDay( isoString, numDays = 1 ) {
			return formatDate( new Date( `${isoString}T00:00` ).getTime() + ( numDays * 24 * 60 * 60 * 1000 ), "yyyy-MM-dd" );
		},

		goToPrevDay() {
			this.emitInput( this.subtractDay( this.value || this.formattedToday ) );
		},

		goToNextDay() {
			this.emitInput( this.addDay( this.value || this.formattedToday ) );
		},

		cancel() {
			this.emitInput( this.initialValue );
			this.open = false;
		},

		save() {
			this.emitInput( this.internalValue );
			this.open = false;
		}
	},

	render() {
		return this.genContainer;
	}
};
